import { Badge } from '@eventbrite/marmalade';
import React, { type ReactElement } from 'react';
import styles from './SignalBadge.module.scss';
import { defineMessage, FormattedMessage, type MessageDescriptor } from 'react-intl';
import { SignalCancel, SignalInfo } from '../Icons';
import { DisplayStatusContextType, useDisplayStatus } from '../contexts/DisplayStatusContext';

export type SignalBadgeStatus = 'soldOut' | 'cancelled' | 'postponed' | 'eventEnded';

type StatusConfig = {
  className: string;
  message: MessageDescriptor;
  icon: ReactElement;
};

type StatusConfigMap = Record<DisplayStatusContextType['displayStatus'], StatusConfig>;

const STATUS_CONFIG: Partial<StatusConfigMap> = {
  SOLD_OUT: {
    className: styles.soldOut,
    message: defineMessage({
      id: 'Sold out',
      defaultMessage: 'Sold out',
      description: 'Event status indicating tickets are sold out',
    }),
    icon: <SignalCancel />,
  },
  CANCELLED: {
    className: styles.cancelled,
    message: defineMessage({
      id: 'Cancelled',
      defaultMessage: 'Cancelled',
      description: 'Event status indicating the event has been cancelled',
    }),
    icon: <SignalCancel />,
  },
  POSTPONED: {
    className: styles.postponed,
    message: defineMessage({
      id: 'Postponed',
      defaultMessage: 'Postponed',
      description: 'Event status indicating the event has been postponed',
    }),
    icon: <SignalInfo />,
  },
  EVENT_ENDED: {
    className: styles.eventEnded,
    message: defineMessage({
      id: 'Event ended',
      defaultMessage: 'Event ended',
      description: 'Event ended event badge message',
    }),
    icon: <SignalInfo />,
  },
  SALES_ENDED: {
    className: styles.eventEnded,
    message: defineMessage({
      id: 'Sales ended',
      defaultMessage: 'Sales ended',
      description: 'Sales ended event badge message',
    }),
    icon: <SignalInfo />,
  },
} as const;

export const SignalBadge: React.FC = () => {
  const { displayStatus: status } = useDisplayStatus();
  const config = status ? STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] : null;

  if (!config) {
    return null;
  }

  return (
    <Badge variant="custom" bordered={true} className={`${styles.badge} ${config.className}`} icon={config.icon}>
      <FormattedMessage id={config.message.id} />
    </Badge>
  );
};
